import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, fmtEUR, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Edit2, Trash2, Wallet, Coins, Calendar, User as UserIcon, CheckCircle2, MinusCircle } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const MONTHS_SHORT = ["gen","feb","mar","apr","mag","giu","lug","ago","set","ott","nov","dic"];

const fmtPeriod = (row, month) => {
  if (!row) return "-";
  const s = row.period_start.split("-");
  const e = row.period_end.split("-");
  return `${s[2]}–${e[2]} ${MONTHS_SHORT[month-1]}`;
};

export default function Uber() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [drivers, setDrivers] = useState([]);
  const [driverId, setDriverId] = useState("");
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { source, week_number, period_start, period_end, label, gross_amount, notes, exists }

  const loadDrivers = useCallback(async () => {
    const r = await api.get("/drivers");
    setDrivers(r.data || []);
    if (r.data && r.data.length && !driverId) setDriverId(r.data[0].id);
  }, [driverId]);

  const loadMatrix = useCallback(async () => {
    if (!driverId) { setMatrix(null); return; }
    setLoading(true);
    try {
      const r = await api.get("/uber-monthly/matrix", { params: { year, month, driver_id: driverId } });
      setMatrix(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setMatrix(null);
    } finally { setLoading(false); }
  }, [year, month, driverId]);

  useEffect(() => { loadDrivers(); }, [loadDrivers]);
  useEffect(() => { loadMatrix(); }, [loadMatrix]);

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y-1, y, y+1, y+2];
  }, [now]);

  const openWeek = (source, row) => {
    setEditing({
      source,
      week_number: row.week_number,
      period_start: row.period_start,
      period_end: row.period_end,
      label: row.label,
      gross_amount: row.gross_amount || 0,
      notes: row.notes || "",
      exists: row.exists,
    });
  };

  const saveWeek = async () => {
    if (!editing) return;
    try {
      await api.put("/uber-monthly/upsert", {
        driver_id: driverId,
        source: editing.source,
        year, month,
        week_number: editing.week_number,
        gross_amount: Number(editing.gross_amount) || 0,
        notes: editing.notes || null,
      });
      toast.success("Settimana salvata");
      setEditing(null);
      loadMatrix();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteWeek = async (source, row) => {
    if (!row.exists) return;
    if (!confirm(`Vuoi eliminare il dato ${source === "uber_cash" ? "Uber Cash" : "Uber"} del periodo ${row.period_start} – ${row.period_end}?`)) return;
    try {
      await api.delete("/uber-monthly/weekly", { params: {
        driver_id: driverId, source, year, month, week_number: row.week_number,
      }});
      toast.success("Settimana eliminata");
      loadMatrix();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const driverName = matrix?.driver_name || "-";
  const rates = matrix?.rates || { uber_commission_rate: 10, uber_driver_share_rate: 50 };
  const uberRows = matrix?.uber || [];
  const cashRows = matrix?.uber_cash || [];
  const totals = matrix?.totals || { uber: 0, uber_cash: 0, total: 0 };

  return (
    <div className="space-y-5" data-testid="uber-page">
      {/* HEADER FILTRI */}
      <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-4">
        <div className="flex-1">
          <div className="font-display text-3xl font-extrabold text-white">Uber &amp; Uber Cash</div>
          <div className="text-xs text-slate-400 mt-1">Gestione settimanale · Autista → Mese → Settimane · IVA {rates.uber_commission_rate}% (solo Uber lordo) · Quota autista {rates.uber_driver_share_rate}%</div>
        </div>
        <div className="grid grid-cols-3 gap-2 md:gap-3 md:min-w-[520px]">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Anno</Label>
            <Select value={String(year)} onValueChange={v=>setYear(Number(v))}>
              <SelectTrigger data-testid="uber-year" className="bg-slate-900 border-slate-800"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800">
                {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Mese</Label>
            <Select value={String(month)} onValueChange={v=>setMonth(Number(v))}>
              <SelectTrigger data-testid="uber-month" className="bg-slate-900 border-slate-800"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800">
                {MONTHS.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Autista</Label>
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger data-testid="uber-driver-select" className="bg-slate-900 border-slate-800"><SelectValue placeholder="Seleziona"/></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800 max-h-80">
                {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {loading && <div className="text-center text-slate-500 py-8">Caricamento…</div>}

      {!loading && matrix && (
        <>
          {/* CARD AUTISTA + PERIODO */}
          <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-r from-amber-500/5 via-transparent to-transparent p-4 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
              <UserIcon size={20} className="text-amber-400"/>
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono-num">Autista · Periodo</div>
              <div className="text-white font-display text-lg font-extrabold" data-testid="uber-driver-name">{driverName} · {MONTHS[month-1]} {year}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Totale complessivo</div>
              <div className="text-amber-400 font-mono-num font-extrabold text-2xl" data-testid="uber-total-grand">{fmtEUR(totals.total)}</div>
            </div>
          </div>

          {/* GRIGLIA UBER */}
          <WeeklyTable
            title="Uber (IVA)"
            subtitle={`IVA ${rates.uber_commission_rate}% detratta`}
            icon={<Wallet size={18} className="text-sky-400"/>}
            tone="sky"
            rows={uberRows}
            source="uber"
            month={month}
            total={totals.uber}
            rates={rates}
            withIva={true}
            onEdit={openWeek}
            onDelete={deleteWeek}
          />

          {/* GRIGLIA UBER CASH */}
          <WeeklyTable
            title="Uber Cash"
            subtitle="Contanti — nessuna IVA"
            icon={<Coins size={18} className="text-emerald-400"/>}
            tone="emerald"
            rows={cashRows}
            source="uber_cash"
            month={month}
            total={totals.uber_cash}
            rates={rates}
            withIva={false}
            onEdit={openWeek}
            onDelete={deleteWeek}
          />

          {/* FOOTER TOTALI */}
          <div className="grid grid-cols-3 gap-3">
            <TotalCard label="Uber" value={totals.uber} icon={<Wallet size={14}/>} tone="sky"/>
            <TotalCard label="Uber Cash" value={totals.uber_cash} icon={<Coins size={14}/>} tone="emerald"/>
            <TotalCard label={`Totale ${MONTHS[month-1]} ${year}`} value={totals.total} icon={<Calendar size={14}/>} tone="amber"/>
          </div>
        </>
      )}

      {!loading && !matrix && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-12 text-center text-slate-500">
          Seleziona un autista per visualizzare la griglia settimanale.
        </div>
      )}

      {/* MODAL EDIT/CREATE SETTIMANA */}
      <Dialog open={!!editing} onOpenChange={(o)=>{ if(!o) setEditing(null); }}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-white">
              {editing?.exists ? "Modifica settimana" : "Inserisci settimana"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono-num">{editing.source === "uber_cash" ? "Uber Cash" : "Uber"} · {editing.week_number}ª Settimana</div>
                <div className="text-white font-display font-bold text-base mt-0.5">{driverName}</div>
                <div className="text-slate-300 text-sm font-mono-num mt-0.5">
                  Periodo: <b>{editing.period_start}</b> → <b>{editing.period_end}</b>
                </div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Importo lordo del periodo (€) *</Label>
                <Input
                  data-testid="uber-week-gross"
                  type="number" step="0.01" min="0"
                  value={editing.gross_amount}
                  onChange={e=>setEditing({...editing, gross_amount: e.target.value})}
                  className="bg-slate-900 border-slate-800 text-lg font-mono-num text-white font-bold"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Note (facoltativo)</Label>
                <Input
                  data-testid="uber-week-notes"
                  value={editing.notes || ""}
                  onChange={e=>setEditing({...editing, notes: e.target.value})}
                  className="bg-slate-900 border-slate-800"
                  placeholder="es. corse weekend"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={()=>setEditing(null)}>Annulla</Button>
                <Button data-testid="uber-week-save" onClick={saveWeek} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">
                  {editing.exists ? "Aggiorna" : "Salva"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WeeklyTable({ title, subtitle, icon, tone, rows, source, month, total, rates, withIva, onEdit, onDelete }) {
  const t = {
    sky: "border-sky-500/25 text-sky-400 bg-sky-500/5",
    emerald: "border-emerald-500/25 text-emerald-400 bg-emerald-500/5",
  }[tone] || "border-slate-800 text-slate-400";
  return (
    <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden" data-testid={`uber-table-${source}`}>
      <div className={`px-5 py-3 border-b border-slate-800 flex items-center justify-between ${t}`}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-slate-950/60 border border-slate-800 flex items-center justify-center">{icon}</div>
          <div>
            <div className="text-white font-display font-extrabold text-base">{title}</div>
            <div className="text-[10px] uppercase tracking-widest opacity-80 font-mono-num">{subtitle}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest opacity-70 font-mono-num">Totale mese</div>
          <div className="font-mono-num font-extrabold text-xl" data-testid={`uber-total-${source}`}>{fmtEUR(total)}</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
            <tr>
              <th className="text-left p-3 w-12">#</th>
              <th className="text-left">Periodo</th>
              <th className="text-right">Lordo</th>
              {withIva && <th className="text-right">IVA {rates.uber_commission_rate}%</th>}
              <th className="text-right">Netto</th>
              <th className="text-right">Quota autista {rates.uber_driver_share_rate}%</th>
              <th className="text-left">Note</th>
              <th className="text-center w-24">Stato</th>
              <th className="text-right pr-3 w-32">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50">
            {rows.length === 0 && <tr><td colSpan={withIva?9:8} className="p-8 text-center text-slate-500">Nessuna settimana per questo mese</td></tr>}
            {rows.map(r => {
              const iva = withIva ? Math.round(r.gross_amount * (rates.uber_commission_rate / 100) * 100) / 100 : 0;
              const net = r.gross_amount - iva;
              const share = Math.round(net * (rates.uber_driver_share_rate / 100) * 100) / 100;
              return (
                <tr key={`${source}-${r.week_number}`} className="hover:bg-slate-900/40" data-testid={`uber-row-${source}-${r.week_number}`}>
                  <td className="p-3 text-amber-400 font-mono-num font-bold">{r.week_number}ª</td>
                  <td className="text-slate-300 font-mono-num">{fmtPeriod(r, month)}</td>
                  <td className={`text-right font-mono-num font-bold ${r.exists ? "text-white" : "text-slate-600"}`}>{fmtEUR(r.gross_amount)}</td>
                  {withIva && <td className="text-right text-rose-400 font-mono-num">{fmtEUR(iva)}</td>}
                  <td className="text-right text-slate-300 font-mono-num">{fmtEUR(net)}</td>
                  <td className="text-right text-amber-400 font-mono-num">{fmtEUR(share)}</td>
                  <td className="text-slate-500 text-xs italic truncate max-w-[180px]">{r.notes || ""}</td>
                  <td className="text-center">
                    {r.exists
                      ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-400 font-mono-num font-bold"><CheckCircle2 size={12}/> Inserito</span>
                      : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num"><MinusCircle size={12}/> Vuoto</span>
                    }
                  </td>
                  <td className="pr-3">
                    <div className="flex justify-end gap-1">
                      <button data-testid={`uber-edit-${source}-${r.week_number}`} onClick={()=>onEdit(source, r)} title={r.exists ? "Modifica" : "Inserisci"} className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-amber-400"><Edit2 size={13}/></button>
                      {r.exists && <button data-testid={`uber-delete-${source}-${r.week_number}`} onClick={()=>onDelete(source, r)} title="Elimina" className="p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-rose-400"><Trash2 size={13}/></button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TotalCard({ label, value, icon, tone }) {
  const t = {
    sky: "border-sky-500/30 bg-sky-500/5 text-sky-400",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-400",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-400",
  }[tone];
  return (
    <div className={`rounded-2xl border p-4 ${t}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80 font-mono-num">{icon}{label}</div>
      <div className="mt-2 font-display font-extrabold text-2xl font-mono-num">{fmtEUR(value)}</div>
    </div>
  );
}
